import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Lock } from "lucide-react";
import Logo from "@/components/Logo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function formatApiError(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => e?.msg || JSON.stringify(e)).join(" ");
  return String(detail);
}

const inputClass =
  "w-full rounded-xl border border-sand/15 bg-basalt/60 px-4 py-3 text-sm text-sand placeholder:text-smoke outline-none transition-[border-color,box-shadow] duration-300 focus:border-ochre/60 focus:ring-2 focus:ring-ochre/20";

export default function AdminLogin() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const res = await fetch(`${API}/auth/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ email, password }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        setError(formatApiError(data.detail));
        return;
      }
      navigate("/admin");
    } catch {
      setError("Network error. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-basalt px-6" data-testid="admin-login-page">
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
        className="glass-card w-full max-w-md rounded-2xl p-10"
      >
        <div className="flex justify-center">
          <Logo />
        </div>
        <h1 className="mt-8 text-center font-display text-2xl font-bold text-sand" data-testid="admin-login-heading">
          Admin Portal
        </h1>
        <p className="mt-2 text-center font-mono text-[10px] uppercase tracking-[0.25em] text-mist">
          Compliance intake dashboard
        </p>
        <form onSubmit={submit} className="mt-8 space-y-5">
          <div>
            <label className="mb-2 block font-mono text-[10px] uppercase tracking-[0.2em] text-mist" htmlFor="admin-email">
              Email
            </label>
            <input id="admin-email" type="email" required className={inputClass} value={email} onChange={(e) => setEmail(e.target.value)} placeholder="admin email" data-testid="admin-email-input" />
          </div>
          <div>
            <label className="mb-2 block font-mono text-[10px] uppercase tracking-[0.2em] text-mist" htmlFor="admin-password">
              Password
            </label>
            <input id="admin-password" type="password" required className={inputClass} value={password} onChange={(e) => setPassword(e.target.value)} placeholder="password" data-testid="admin-password-input" />
          </div>
          {error && (
            <p className="rounded-lg border border-red-500/40 bg-red-500/10 px-4 py-3 text-sm text-red-400" data-testid="admin-login-error">
              {error}
            </p>
          )}
          <button
            type="submit"
            disabled={loading}
            className="flex w-full items-center justify-center gap-2 rounded-full bg-ochre px-8 py-4 font-display text-sm font-bold uppercase tracking-wider text-basalt transition-[background-color,opacity] duration-300 hover:bg-ochre-light disabled:opacity-60"
            data-testid="admin-login-submit-button"
          >
            <Lock size={15} />
            {loading ? "Signing in..." : "Sign In"}
          </button>
        </form>
        <div className="mt-6 text-center">
          <Link to="/" className="font-mono text-[10px] uppercase tracking-[0.2em] text-smoke transition-colors duration-300 hover:text-ochre" data-testid="back-to-site-link">
            Back to site
          </Link>
        </div>
      </motion.div>
    </div>
  );
}
